import "@/styles";

import { ThemeProvider as MUIThemeProvider } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import actions from "@/actions";
import { Loading } from "@/components";
import MessageDisplay from "@/components/core/layout/MessageDisplay";
import theme from "@/styles/theme";
import { ConfigurationManager } from "@/utils";
import PrintContext from "@/utils/print-context";

import AdvancedOperations from "./advanced-operations";
import { MapContainer } from "./map-container";

const App = ({ setOrientation, orientation, loading, loadRequirements }) => {
    const [mapContext, setMapContext] = useState(null);

    useEffect(() => {
        if (!ConfigurationManager.values.floorSpaceId) {
            loadRequirements();
        }
    }, [loadRequirements]);

    const onResizeWindows = useCallback(() => {
        const newOrientation =
            window.innerHeight > window.innerWidth ? "portrait" : "landscape";
        if (orientation !== newOrientation) {
            setOrientation(newOrientation);
        }
    }, [orientation, setOrientation]);

    useEffect(() => {
        window.addEventListener("resize", onResizeWindows);
        return () => {
            window.removeEventListener("resize", onResizeWindows);
        };
    }, [onResizeWindows]);

    const setContext = useCallback((context) => {
        setMapContext(context);
    }, []);

    const pageStyle = {
        display: "flex",
        flexDirection: "column",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
    };

    return (
        <div style={pageStyle}>
            <PrintContext
                value={{
                    context: mapContext,
                    setContext: setContext,
                }}
            >
                <MUIThemeProvider theme={theme}>
                    <MapContainer />
                    <AdvancedOperations />
                    {loading.active && (
                        <Loading
                            message={loading.message}
                            type={loading.type}
                        />
                    )}
                    <MessageDisplay context="global" />
                </MUIThemeProvider>
            </PrintContext>
        </div>
    );
};

App.propTypes = {
    setOrientation: PropTypes.func.isRequired,
    orientation: PropTypes.oneOf(["portrait", "landscape"]),
    loading: PropTypes.shape({
        active: PropTypes.bool,
        message: PropTypes.string,
        type: PropTypes.string,
    }),
    loadRequirements: PropTypes.func.isRequired,
};

const mapStateToProps = ({ window }) => ({
    orientation: window.orientation,
    loading: window.loading,
});
const mapDispatchToProps = {
    setOrientation: actions.window.setOrientation,
    loadRequirements: actions.map.general.loadRequirements,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
